'use client'

/**
 * The states one region of a federal screen can be in, and the primitives both the
 * Agent Hub and Auto Insights compose from.
 *
 * 63-R079 requires five distinct region states and forbids rendering a zero, a
 * zero-length bar or an empty chart frame in place of "nothing has been recorded".
 * 63-R081 requires one failing region to leave every other region rendered.
 * 30-R098 states the same five states for the console as a whole. This file is the
 * one implementation both screens use, so the two cannot drift.
 *
 * `frontend/src/components/scc/common/ResourceBoundary.tsx` in TDD 30 section 4.3 is
 * the console-wide version of this component and supersedes this file when it lands.
 * These two screens were built before it existed and say so here rather than
 * shipping a second private copy quietly.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import { cn } from '@/lib/utils'
import type { Probe } from './federal-fetch'

/** A small caps label, on the inherited `text-micro` token. */
export function MicroLabel({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <span className={cn('text-micro uppercase text-brand-muted', className)}>{children}</span>
  )
}

/** One region of a screen. Header, optional note, optional right-hand control. */
export function SectionCard({
  title,
  note,
  right,
  children,
  className,
}: {
  title: string
  note?: ReactNode
  right?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <Card className={className}>
      <CardContent className="p-6">
        <div className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5">
            <h2 className="font-display text-base font-semibold tracking-tight text-brand-navy">
              {title}
            </h2>
            {note ? <p className="max-w-3xl text-sm text-muted-foreground">{note}</p> : null}
          </div>
          {right ? <div className="shrink-0">{right}</div> : null}
        </div>
        <div className="mt-4">{children}</div>
      </CardContent>
    </Card>
  )
}

/**
 * A figure that does not exist yet, rendered visibly rather than as a plausible
 * number. Master prompt 13.8, and 63-R005 requires what would establish it to be
 * named beside it.
 */
export function FigureNeeded({ establishedBy }: { establishedBy?: string }) {
  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-2">
      <span className="rounded-md bg-secondary px-1.5 py-0.5 font-mono text-xs font-semibold text-brand-navy">
        [FIGURE NEEDED]
      </span>
      {establishedBy ? (
        <span className="text-xs text-muted-foreground">{establishedBy}</span>
      ) : null}
    </span>
  )
}

/**
 * Nothing has been recorded, which is not the same statement as zero. 63-R010 makes
 * these two distinct return values and this is the second of them at the interface.
 */
export function NoObservations({
  measure,
  writtenBy,
}: {
  measure: string
  writtenBy?: string | null
}) {
  return (
    <div className="flex items-start gap-2 text-sm text-muted-foreground">
      <Icons.circle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-muted" strokeWidth={1.5} />
      <span>
        No observations. Nothing has been recorded for {measure}.
        {writtenBy ? <> The rows it reads are written by {writtenBy}.</> : null}
      </span>
    </div>
  )
}

/** Loading, shaped to the rows it replaces rather than a spinner. 30-R098. */
export function RegionSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2" aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }).map((_, index) => (
        <Skeleton key={index} className="h-9 w-full" />
      ))}
    </div>
  )
}

/**
 * The region could not be read, with the server's own message and a retry. The
 * console never rewrites a server message, per 30-R098.
 */
export function RegionError({
  message,
  httpStatus,
  onRetry,
}: {
  message: string | null
  httpStatus: number | null
  onRetry?: () => void
}) {
  return (
    <div className="space-y-3 rounded-xl border border-red-200 bg-red-50 p-4">
      <div className="flex items-start gap-2">
        <Icons.alertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" strokeWidth={1.75} />
        <div className="space-y-1">
          <p className="text-sm font-semibold text-red-700">
            This region failed to load{httpStatus ? ` with HTTP ${httpStatus}` : ''}.
          </p>
          <p className="text-sm text-red-700">{message || 'The server gave no message.'}</p>
        </div>
      </div>
      {onRetry ? (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <Icons.refresh className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.75} />
          Retry
        </Button>
      ) : null}
    </div>
  )
}

/** A denial, with the decision's reason verbatim. 30-R103, 61-R101, 63-R088. */
export function PermissionNotice({ message }: { message: string | null }) {
  return (
    <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4">
      <Icons.lock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" strokeWidth={1.75} />
      <div className="space-y-1">
        <p className="text-sm font-semibold text-amber-700">This read was not permitted.</p>
        <p className="text-sm text-amber-700">
          {message || 'The server gave no reason, which is itself a defect.'}
        </p>
      </div>
    </div>
  )
}

/**
 * The route is not mounted in this build. Named as its own state because 63-R080
 * requires an operator seeing an empty region to be able to tell whether nothing has
 * happened or nothing is recorded, and a 404 is the second of those.
 */
export function AwaitingWriter({
  path,
  writtenBy,
  showsMeanwhile,
}: {
  path: string
  writtenBy: string
  showsMeanwhile?: ReactNode
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 rounded-xl border border-border bg-secondary p-4">
        <Icons.info className="mt-0.5 h-4 w-4 shrink-0 text-brand-muted" strokeWidth={1.75} />
        <div className="space-y-1">
          <p className="text-sm font-semibold text-brand-navy">
            No observations. This surface has no writer in this build.
          </p>
          <p className="text-sm text-muted-foreground">
            <span className="font-mono text-xs">{path}</span> answered 404. {writtenBy}
          </p>
        </div>
      </div>
      {showsMeanwhile}
    </div>
  )
}

/**
 * One region, six states, one place. The resolved content is a function of the data
 * so a caller cannot reach `probe.data` without passing through the states first.
 *
 * `not_requested` is separate from `loading` on purpose. A region whose path depends
 * on a selection nobody has made yet is not waiting on the network, and rendering a
 * skeleton for it tells a reader the server is slow when the truth is that the screen
 * is waiting for them. 63-R080.
 */
export function RegionStates<T>({
  probe,
  path,
  writtenBy,
  skeletonRows = 3,
  onRetry,
  showsMeanwhile,
  notRequestedStatement,
  children,
}: {
  probe: Probe<T>
  /** Shown on the absent state so a reader can check the route themselves. */
  path: string
  /** One sentence naming what writes the rows this region reads. */
  writtenBy: string
  skeletonRows?: number
  onRetry?: () => void
  /** Rendered under the absent notice: what the screen can still show honestly. */
  showsMeanwhile?: ReactNode
  /** What the reader must choose before this region can be read. */
  notRequestedStatement?: ReactNode
  children: (data: T) => ReactNode
}) {
  if (probe.status === 'not_requested')
    return (
      <p className="text-sm text-muted-foreground">
        {notRequestedStatement ?? (
          <>
            Nothing has been requested for this region yet. It is served at{' '}
            <span className="font-mono text-xs">{path}</span> once a selection names one.
          </>
        )}
      </p>
    )
  if (probe.status === 'loading') return <RegionSkeleton rows={skeletonRows} />
  if (probe.status === 'absent')
    return <AwaitingWriter path={path} writtenBy={writtenBy} showsMeanwhile={showsMeanwhile} />
  if (probe.status === 'unauthorized') return <PermissionNotice message={probe.message} />
  if (probe.status === 'error')
    return <RegionError message={probe.message} httpStatus={probe.httpStatus} onRetry={onRetry} />
  return <>{probe.data === null ? null : children(probe.data)}</>
}

/**
 * A boundary around ONE region's rendered content. 63-R081.
 *
 * The requirement is that one failing measure renders as an error in its own row while
 * every other measure still renders. The server-reported half of that is handled per
 * cell. This is the other half: a row the server sent successfully and the console
 * could not render, a shape the declaration did not predict, costs this region and
 * not the tab, and certainly not the document.
 *
 * `fallback` receives the error so the caller can say which region failed. A caller
 * that does not name its region gets a statement that says the figures here are not
 * trustworthy, never "something went wrong": a region that fails silently or vaguely on
 * a measures surface is read as an empty estate.
 *
 * A class component because React exposes no hook for this; `componentDidCatch` is the
 * only interface there is.
 */
export class RegionBoundary extends Component<
  { children: ReactNode; label: string },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(`[scc] region boundary caught in ${this.props.label}`, error, info)
  }

  render() {
    if (this.state.error === null) return this.props.children
    return (
      <div
        className="space-y-2 rounded-xl border border-red-200 bg-red-50 p-4"
        role="alert"
      >
        <p className="text-sm font-semibold text-red-700">
          {this.props.label} could not be rendered from the response.
        </p>
        <p className="text-sm text-red-700">
          The server answered and this console could not read the answer, so nothing in
          this region is a statement about the estate. Every other region on this screen
          is unaffected and still reflects its own response.
        </p>
        <p className="font-mono text-xs text-red-700">{this.state.error.message}</p>
      </div>
    )
  }
}

/** A table that scrolls inside its own region rather than widening the page. */
export function DataTable({
  head,
  children,
  className,
}: {
  head: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="w-full min-w-[36rem] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-left">{head}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

export function Th({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <th
      scope="col"
      className={cn('py-2 pr-4 align-bottom text-micro uppercase text-brand-muted', className)}
    >
      {children}
    </th>
  )
}

export function Td({
  children,
  className,
  title,
}: {
  children: ReactNode
  className?: string
  /** The server's own value, where the cell renders a formatted form of it. */
  title?: string
}) {
  return (
    <td className={cn('py-2.5 pr-4 align-top text-sm text-foreground', className)} title={title}>
      {children}
    </td>
  )
}

/** A count in a table column. Tabular figures so columns of numbers align. */
export function Count({ value }: { value: number | null }) {
  if (value === null) {
    return <span className="text-sm text-brand-muted">not measured</span>
  }
  return <span className="text-sm tabular-nums text-foreground">{value}</span>
}

/**
 * A rate with its numerator and denominator both visible. 63-R009: a rate whose
 * denominator is not visible cannot be judged, because 100 percent of two
 * observations and 100 percent of two hundred are different claims.
 */
export function RateWithDenominator({
  numerator,
  denominator,
  measure,
  writtenBy,
}: {
  numerator: number | null
  denominator: number | null
  measure: string
  writtenBy?: string | null
}) {
  if (denominator === null || denominator === 0 || numerator === null) {
    return <NoObservations measure={measure} writtenBy={writtenBy} />
  }
  const rate = (numerator / denominator) * 100
  return (
    <span className="inline-flex items-baseline gap-2">
      <span className="text-sm font-semibold tabular-nums text-brand-navy">
        {rate.toFixed(1)}%
      </span>
      <span className="text-xs tabular-nums text-muted-foreground">
        {numerator} of {denominator}
      </span>
    </span>
  )
}
